
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import config from '../common/Config';
import { format } from 'date-fns';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import * as XLSX from 'xlsx';
import { Box, Grid, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, TablePagination, Skeleton, Alert, Link as MuiLink, TextField, Chip, useMediaQuery, InputAdornment, Grid2, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Select, FormControl, TableSortLabel, Tooltip } from '@mui/material';
import { Iconify } from 'src/index';
import { toast } from 'sonner';
import { isRhpEposTenant, transformProductToCartItem, buildAddToCartPayload } from './Epos/utils/cartCalculations';
import {
	getRhpInvoicePrintUrl,
	getRhpV2InvoicePrintUrl,
	getRhpLegacyInvoicePrintUrl,
	getRhpElectronInvoicePrintUrl,
	getRhpA4InvoicePrintUrl,
	isRhpPrintTenant,
	shouldUseRhpInvoicePrint,
} from './Epos/utils/rhpInvoicePrint';
import { getCachedCompanySettings } from '../../utils/companySettingsCache';
import { fetchProformaInvoice } from '../../utils/proformaInvoiceApi';
import { addToCart, clearCartByCustomer, PLATFORMS } from '../../data/cart';
import { formatCustomerDisplayLabel } from './Epos/component/CustomerSearch';

const isGetOrFreeInvoiceLine = (product) => {
	if (!product) return true;
	const bog = String(
		product.buyOrGet || product.buy_or_get || product.promo_type || product.promoType || ''
	)
		.trim()
		.toUpperCase();
	if (bog === 'GET' || bog === 'FREE') return true;
	if (product.free_product === '1' || product.free_product === '(F)') return true;
	return false;
};

const mapInvoiceProductForReorder = (product) => {
	const productId = product.product_id || product.id;
	const qtyRaw = product.quantity ?? product.order_qty ?? product.qty ?? 1;
	const orderTypeRaw = String(product.order_type || product.piece_type || product.type || 'Box');
	const orderType = orderTypeRaw.toLowerCase() === 'piece' ? 'Piece' : 'Box';
	const unitPrice = parseFloat(
		product.unit_price ?? product.discount_price ?? product.price ?? product.offer_price ?? 0
	) || 0;

	return {
		id: productId,
		product_id: productId,
		product_code: product.product_code || product.code || '',
		code: product.product_code || product.code || '',
		product_name: product.product_name || product.name || '',
		name: product.product_name || product.name || '',
		qty: String(qtyRaw),
		quantity: qtyRaw,
		order_type: orderType,
		type: orderType,
		unit_price: unitPrice,
		price: unitPrice,
		split_price: product.split_price ?? product.piece_price ?? '',
		is_promoted: '0',
		promo_id: '',
		promo_type: '',
		buyOrGet: '',
		tax_rate: product.tax_rate_percent ?? product.tax_rate ?? product.rate ?? '0',
		tax_code: product.tax_code ?? '',
		split: product.split ?? '0',
		noOfPieceInBox: product.noOfPieceInBox ?? product.no_of_piece_in_box ?? '1',
	};
};

const allColumns = [
	{ id: 'date', label: 'Order Date', numeric: false, width: config.PREFIX === 'rhp' ? 150 : 100 },
	{ id: 'reference_no', label: 'Order Id', numeric: false, width: 80 },
	{ id: 'customer', label: 'Customers (A/C No)', numeric: false, width: 250 },
	{ id: 'postal_code', label: 'Post Code', numeric: false, width: 100 },
	{ id: 'route_number', label: 'Route', numeric: false, width: 70 },
	{ id: 'grand_total', label: 'Order Amt', numeric: true, width: 100 },
	{ id: 'profit', label: 'Profit(%)', numeric: true, width: 90 },
	{ id: 'prev_due', label: 'C.Bal', numeric: true, width: 100 },
	{ id: 'amtDue', label: 'Amt.Due', numeric: true, width: 100 },
	{ id: 'name', label: 'Order By', numeric: false, width: 90 },
	{ id: 'print', label: 'Print Status', numeric: false, width: 100 },
	{ id: 'sale_status', label: 'Status', numeric: false, width: 120 }
];

const columns =
	config.PREFIX === 'rhp'
		? allColumns.filter((column) => column.id !== 'prev_due' && column.id !== 'amtDue' && column.id !== 'profit')
		: allColumns;

const numericColumns = new Set(
	config.PREFIX === 'rhp'
		? ['grand_total', 'profit']
		: ['grand_total', 'profit', 'prev_due', 'amtDue']
);

const parseNumeric = value => {
	const numeric = parseFloat(String(value ?? '0').replace(/,/g, ''));
	return Number.isFinite(numeric) ? numeric : 0;
};

const formatTotalValue = value => {
	const numeric = parseNumeric(value);
	return numeric.toFixed(2);
};

const getOrderByValue = row => {
	const name = row?.name;
	if (name != null && String(name).trim() !== '') {
		return String(name).trim();
	}
	const documentCreatedBy = row?.document_created_by;
	return (documentCreatedBy != null && String(documentCreatedBy).trim() !== '') ? String(documentCreatedBy).trim() : '';
};

const createEmptyTotals = () => ({
	grand_total: '0.00'
});

const getPaymentStatusChipConfig = status => {
	if (!status) {
		return { color: 'default', variant: 'outlined' };
	}
	const normalized = status.toLowerCase();
	if (normalized === 'paid') {
		return { color: 'success', variant: 'soft' };
	}
	if (normalized === 'due' || normalized === 'pending') {
		return { color: 'warning', variant: 'soft' };
	}
	return { color: 'default', variant: 'outlined' };
};

const getChannelByRole = () => {
	try {
		const activeRoleId = localStorage.getItem('active_role_id');
		const userRolesData = localStorage.getItem('user_roles_data');

		if (activeRoleId && userRolesData) {
			const roles = JSON.parse(userRolesData);
			const activeRole = roles.find(role => role.id.toString() === activeRoleId.toString());

			if (activeRole) {
				if (activeRole.name === 'Tele Calling' || activeRole.id === '20' || activeRole.id === 20) {
					return 5;
				}
				if (activeRole.name === 'Till' || activeRole.id === '18' || activeRole.id === 18) {
					return 4;
				}
				const roleName = String(activeRole.name || '').toLowerCase();
				if (
					activeRole.id === '22' ||
					activeRole.id === 22 ||
					roleName.includes('account')
				) {
					return '';
				}
			}
		}

		if (activeRoleId) {
			if (activeRoleId === '20' || activeRoleId === 20) {
				return 5;
			}
			if (activeRoleId === '18' || activeRoleId === 18) {
				return 4;
			}
			if (activeRoleId === '22' || activeRoleId === 22) {
				return '';
			}
		}

		return 5;
	} catch (error) {
		return 5;
	}
};

const EposSales = () => {
	const [dates, setDates] = useState([null, null]);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [data, setData] = useState([]);
	const [totalRows, setTotalRows] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [totals, setTotals] = useState(createEmptyTotals);
	const [sortBy, setSortBy] = useState('');
	const [sortDirection, setSortDirection] = useState('asc');
	const [refreshKey, setRefreshKey] = useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [orderByFilter, setOrderByFilter] = useState('');
	const [actionAnchorEl, setActionAnchorEl] = useState(null);
	const [actionRow, setActionRow] = useState(null);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [rejectData, setRejectData] = useState(null);
	const [rejectComment, setRejectComment] = useState('');
	const [showHoldModal, setShowHoldModal] = useState(false);
	const [holdReason, setHoldReason] = useState('');
	const [holdOrderId, setHoldOrderId] = useState('');
	const [reorderConfirmOpen, setReorderConfirmOpen] = useState(false);
	const [reorderRow, setReorderRow] = useState(null);
	const [statusTab, setStatusTab] = useState(() => {
		try {
			const queryTab = new URLSearchParams(window.location.search).get('statusTab');
			if (queryTab === 'Delivered' || queryTab === 'New') {
				return queryTab;
			}
			const saved = sessionStorage.getItem('epos_sales_status_tab');
			return saved === 'Delivered' || saved === 'New' ? saved : 'New';
		} catch {
			return 'New';
		}
	}); // 'New' | 'Delivered'
	const accessToken = localStorage.getItem('accessToken');
	const token = localStorage.getItem('token');
	const userId = localStorage.getItem('userId');
	const username = localStorage.getItem('username');
	const isCompactTable = useMediaQuery('(max-width:900px)');
	const isRhpUser = isRhpEposTenant();
	const userRole = String(localStorage.getItem('user_role') || '').trim();
	const canEditEposSales = !isRhpUser || userRole !== '18';

	useEffect(() => {
		if (!accessToken) {
			window.location.href = `${config.BASE_URL}/login`;
		}
	}, [accessToken]);

	const handleDateChange = (value) => {
		const range = Array.isArray(value) ? value : [null, null];
		setDates(range);
		const start = range[0] ? format(range[0], 'yyyy-MM-dd') : '';
		const end = range[1] ? format(range[1], 'yyyy-MM-dd') : '';
		setStartDate(start);
		setEndDate(end);
		setPage(0);
	};

	useEffect(() => {
		if (!accessToken) {
			return;
		}
		let active = true;
		setLoading(true);
		const params = new URLSearchParams();
		params.append('draw', String(page + 1));
		params.append('start', String(page * rowsPerPage));
		params.append('length', String(rowsPerPage));
		params.append('searchValue', searchTerm);
		params.append('sale_status', statusTab);
		params.append('channels', String(getChannelByRole()));
		params.append('search[value]', searchTerm);
		params.append('search[regex]', 'false');
		params.append('searchByFromdate', startDate);
		params.append('searchByTodate', endDate);
		const orderColumnIndex = sortBy ? Math.max(columns.findIndex(col => col.id === sortBy), 0) : 0;
		params.append('order[0][column]', String(orderColumnIndex));
		params.append('order[0][dir]', sortBy ? sortDirection : 'desc');
		columns.forEach((column, index) => {
			params.append(`columns[${index}][data]`, column.id);
			params.append(`columns[${index}][name]`, '');
			params.append(`columns[${index}][searchable]`, 'false');
			params.append(`columns[${index}][orderable]`, 'false');
			params.append(`columns[${index}][search][value]`, '');
			params.append(`columns[${index}][search][regex]`, 'false');

		});
		axios
			.post(`${config.API_URL}/sales/saleslistv1`, params, {
				headers: {
					authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/x-www-form-urlencoded'

				}
			})
			.then(response => {
				if (!active) {
					return;
				}
				const json = response.data || {};
				const rows = Array.isArray(json.data) ? json.data : [];
				setData(rows);
				const filteredCount = Number(json.recordsFiltered);
				const totalCount = Number.isFinite(filteredCount) ? filteredCount : Number(json.recordsTotal) || rows.length;
				setTotalRows(totalCount);
				const grandTotal = rows.reduce((sum, row) => sum + parseNumeric(row.grand_total || 0), 0);
				setTotals({
					grand_total: formatTotalValue(grandTotal)
				});
				setError('');
			})
			.catch(err => {
				if (!active) {
					return;
				}
				const status = err.response?.status;
				const message = err.response?.data?.message || err.message || 'Failed to load sales.';
				setError(status ? `Error ${status}: ${message}` : message);
				setData([]);
				setTotalRows(0);
				setTotals(createEmptyTotals());
			})
			.finally(() => {
				if (!active) {
					return;
				}
				setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [accessToken, page, rowsPerPage, startDate, endDate, searchTerm, sortBy, sortDirection, refreshKey, statusTab]);

	const handleSubmit = () => {
		setPage(0);
		setRefreshKey(value => value + 1);
	};

	// Restore this user's last-selected "Order By" sales rep on load, saved server-side
	// (sma_user table) so it persists across refresh and across browsers/devices.
	useEffect(() => {
		if (!accessToken || !userId) {
			return;
		}
		let active = true;
		axios
			.get(`${config.API_URL}/ws-get-sales-order-by-preference`, {
				params: { userId },
				headers: { authorization: `Bearer ${accessToken}` }
			})
			.then(response => {
				if (!active) {
					return;
				}
				const saved = response.data?.order_by;
				if (saved != null && String(saved).trim() !== '') {
					setOrderByFilter(String(saved).trim());
				}
			})
			.catch(() => { });
		return () => {
			active = false;
		};
	}, [accessToken, userId]);

	const saveOrderByPreference = (value) => {
		if (!accessToken || !userId) {
			return;
		}
		const params = new URLSearchParams();
		params.append('userId', userId);
		params.append('order_by', value);
		axios
			.post(`${config.API_URL}/ws-save-sales-order-by-preference`, params, {
				headers: {
					authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/x-www-form-urlencoded'
				}
			})
			.catch(() => { });
	};

	const handleOrderByChange = (value) => {
		setOrderByFilter(value);
		saveOrderByPreference(value);
	};

	const handleClearFilters = () => {
		setDates([null, null]);
		setStartDate('');
		setEndDate('');
		setSearchTerm('');
		handleOrderByChange('');
		// Keep current tab (New/Delivered) when clearing filters
		setPage(0);
		setRefreshKey(value => value + 1);
	};

	const handleStatusTabChange = (event, newValue) => {
		setStatusTab(newValue);
		try {
			sessionStorage.setItem('epos_sales_status_tab', newValue);
		} catch (_) { }
		setPage(0);
		setRefreshKey(value => value + 1);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSort = columnId => {
		if (sortBy === columnId) {
			setSortDirection(direction => (direction === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortBy(columnId);
			setSortDirection('asc');
		}
	};

	const isOrderEditOpenByAnotherUser = row => {
		if (Number(row?.is_open) !== 1) {
			return false;
		}
		const openedId = String(row?.opened_id ?? '');
		const myId = String(userId ?? '');
		if (!myId) {
			return true;
		}
		return openedId !== myId;
	};

	const orderByOptions = useMemo(() => {
		const set = new Set();
		data.forEach(row => {
			const value = getOrderByValue(row);
			if (value) {
				set.add(value);
			}
		});
		return Array.from(set).sort((a, b) => a.localeCompare(b));
	}, [data]);

	const sortedData = useMemo(() => {
		const filtered = orderByFilter
			? data.filter(row => getOrderByValue(row) === orderByFilter)
			: data;
		if (!sortBy) {
			return filtered;
		}
		const columnExists = columns.some(col => col.id === sortBy);
		if (!columnExists) {
			return filtered;
		}
		const sorted = [...filtered].sort((a, b) => {
			const aValue = numericColumns.has(sortBy) ? parseNumeric(a[sortBy]) : a[sortBy] || '';
			const bValue = numericColumns.has(sortBy) ? parseNumeric(b[sortBy]) : b[sortBy] || '';
			if (numericColumns.has(sortBy)) {
				return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
			}
			const result = String(aValue).localeCompare(String(bValue));
			return sortDirection === 'asc' ? result : -result;
		});
		return sorted;
	}, [data, sortBy, sortDirection, orderByFilter]);

	const paginationSx = useMemo(
		() => ({
			'& .MuiTablePagination-toolbar': {
				flexWrap: 'nowrap',
				alignItems: 'baseline',

				px: 0
			},
			'& .MuiTablePagination-displayedRows': {
				marginLeft: 0
			}
		}),
		[]
	);

	const handleExport = () => {
		if (!sortedData.length) {
			return;
		}
		const headers = [
			'Date',
			'Invoice No',
			'Customer',
			'Grand Total',
			'Paid',
			'Cash Amt',
			'Cheque Amt',
			'Card Amt',
			'Credit Facility',
			'Status',
			'Payment Status'
		];
		const rows = sortedData.map(row => [
			row.date,
			row.reference_no,
			row.customer,
			row.grand_total,
			row.paid,
			row.cash_amount,
			row.cheque_amount,
			row.card_amount,
			row.credit_card_amount,
			row.sale_status === 'Delivered' ? 'Completed' : row.sale_status,
			row.payment_status
		]);
		const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'EPOS Sales');
		XLSX.writeFile(workbook, 'epos_sales.xlsx');
	};

	const renderCellContent = (columnId, row) => {
		if (columnId === 'reference_no') {
			const isHold = row.sale_status === 'Hold';
			const staffNote = String(row.staff_note || row.staffNote || row.note || '').trim();
			const reasonNote = String(
				row.hold_reason || row.reason_note || row.reject_reason || row.reason || ''
			).trim();
			const noteTooltip = [reasonNote, staffNote].filter(Boolean).join('\n');
			const noteIconColor = isHold ? '#f14240' : '#0456f1';

			const orderIdNode = (() => {
				if (!canEditEposSales) {
					return (
						<Typography component="span" sx={{ color: '#212b36', fontWeight: 500, fontSize: '0.75rem', flexShrink: 0 }}>
							{row.reference_no}
						</Typography>
					);
				}
				const isDelivered = row.sale_status === 'Delivered';
				let baseUrl;
				if (isDelivered) {
					baseUrl = `${config.BASE_URL}/sales/epos_sales/view/${row.ids}`;
				} else {
					baseUrl = accessToken === 'sdm'
						? `${config.BASE_URL}/sales/edit_invoice_sdm/${row.ids}`
						: `${config.BASE_URL}/sales/epos_sales/edit/${row.ids}`;
				}
				const blockedByOther = !isDelivered && isOrderEditOpenByAnotherUser(row);
				return (
					<MuiLink
						href={baseUrl}
						underline="always"
						target="_self"
						rel="noopener noreferrer"
						onClick={event => {
							if (blockedByOther) {
								event.preventDefault();
								const who = row.opened_by != null && String(row.opened_by).trim() !== '' ? String(row.opened_by).trim() : 'another user';
								toast.error(`This order is opened by: ${who}`);
							}
						}}
						sx={{ color: isHold ? '#f14240' : '#212b36', fontWeight: 500, textDecoration: 'underline !important', fontSize: '0.75rem', flexShrink: 0 }}
					>
						{row.reference_no}
					</MuiLink>
				);
			})();

			return (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 0.5,
						minWidth: 0,
						maxWidth: '100%',
					}}
				>
					{orderIdNode}
					{noteTooltip ? (
						<Tooltip
							title={noteTooltip}
							arrow
							placement="top"
							enterDelay={200}
							slotProps={{
								tooltip: {
									sx: {
										maxWidth: 320,
										whiteSpace: 'pre-wrap',
										fontSize: '0.75rem',
									},
								},
							}}
						>
							<Box
								component="span"
								sx={{
									display: 'inline-flex',
									alignItems: 'center',
									cursor: 'pointer',
									flexShrink: 0,
								}}
							>
								<Iconify
									icon="eva:info-fill"
									width={14}
									sx={{ color: noteIconColor }}
								/>
							</Box>
						</Tooltip>
					) : null}
				</Box>
			);
		}
		if (columnId === 'sale_status') {
			return (
				<Chip
					label={row.sale_status}
					variant="soft"
					size="small"
					sx={{
						fontWeight: 500,
						fontSize: '0.7rem',
						px: 1,
						backgroundColor: 'transparent',
						'&.MuiChip-root': { backgroundColor: 'transparent' },
						color: 'inherit',
						'& .MuiChip-label': { color: 'inherit' }
					}}
				/>
			);
		}
		if (columnId === 'date') {
			return isRhpUser ? (row.date_time ?? row.date ?? '') : (row.date ?? '');
		}
		if (columnId === 'name') {
			return getOrderByValue(row);
		}
		if (columnId === 'print') {
			return String(row.print || '').trim().toUpperCase() === 'Y' ? 'Printed' : 'Not Printed';
		}
		const value = row[columnId];
		const column = columns.find((col) => col.id === columnId);
		if (column?.numeric) {
			return value;
		}
		return value ?? '';
	};

	const handleActionMenuOpen = (event, row) => {
		setActionAnchorEl(event.currentTarget);
		setActionRow(row);
	};

	const handleActionMenuClose = () => {
		setActionAnchorEl(null);
		setActionRow(null);
	};

	const handleRejectClick = (row) => {
		setRejectData(row);
		setRejectComment('');
		setShowRejectModal(true);
		handleActionMenuClose();
	};

	const handleRejectSubmit = () => {
		if (!rejectComment) {
			toast.error('Please enter reason for reject.');
			return;
		}
		const formData = new FormData();
		formData.append('status', 'Reject');
		formData.append('reason_note', rejectComment);
		formData.append('new-sales', 'Yes');
		formData.append('id', rejectData.ids);
		axios
			.post(`${config.API_URL}/ws-update-status`, formData, {
				headers: { authorization: `Bearer ${token}` }
			})
			.then(() => {
				setShowRejectModal(false);
				setRejectComment('');
				toast.success('Order rejected successfully');
				setRefreshKey(value => value + 1);
			})
			.catch(() => {
				toast.error('Error rejecting order');
			});
	};

	const handleHoldClick = (row) => {
		setHoldOrderId(row.ids);
		setHoldReason('');
		setShowHoldModal(true);
		handleActionMenuClose();
	};

	const handleHoldSubmit = () => {
		if (!holdReason) {
			toast.error('Please enter reason for hold.');
			return;
		}
		const formData = new FormData();
		formData.append('status', 'Hold');
		formData.append('reason_note', holdReason);
		formData.append('id', holdOrderId);
		formData.append('new-sales', 'No');
		axios
			.post(`${config.API_URL}/ws-update-status`, formData, {
				headers: { authorization: `Bearer ${token}` }
			})
			.then(() => {
				setShowHoldModal(false);
				toast.success('Order Hold successfully');
				setRefreshKey(value => value + 1);
			})
			.catch(() => {
				toast.error('Error holding order');
			});
	};

	const handleConfirmInvoice = (saleId) => {
		if (!saleId) {
			toast.error('Sales id missing');
			return;
		}
		const formData = new FormData();
		formData.append('sales_id', saleId);
		axios
			.post(`${config.API_URL}/ws_confirm_sales_invoice`, formData, {
				headers: { authorization: `Bearer ${accessToken}` }
			})
			.then((response) => {
				const data = response?.data;
				if (data && data.success === false) {
					toast.error(data.message || 'Failed to confirm invoice');
					return;
				}
				toast.success(data?.message || 'Invoice confirmed successfully');
				setRefreshKey((value) => value + 1);
			})
			.catch(() => {
				toast.error('Error confirming invoice');
			});
	};

	const isRhpPrintTenantLocal = () => isRhpPrintTenant();

	// RHP-tenant "print invoice" audit: records print=Y, print_by, print_on against the
	// order's sma_sales row whenever an RHP user prints an invoice.
	const markSalePrinted = (id) => {
		if (!accessToken || !id) {
			return;
		}
		const params = new URLSearchParams();
		params.append('orderId', id);
		params.append('print', 'Y');
		params.append('print_by', username || '');
		params.append('print_on', new Date().toISOString());
		axios
			.post(`${config.API_URL}/ws-mark-sale-printed`, params, {
				headers: {
					authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/x-www-form-urlencoded'
				}
			})
			.catch(() => { });
	};

	const getDeliveryNotePrintUrl = (id) => {
		const accessToken = localStorage.getItem('accessToken');
		if (isRhpPrintTenantLocal()) {
			return `${config.BASE_URL}/invoice/rhp_delivery_note.html?orderId=${id}`;
		}
		return `${config.BASE_URL}/invoice/sales_invoice.html?orderId=${id}&prefix=${encodeURIComponent(accessToken || '')}`;
	};

	const getDrinkSheetPrintUrl = (id) => {
		const accessToken = localStorage.getItem('accessToken');
		if (isRhpPrintTenantLocal()) {
			return `${config.BASE_URL}/invoice/rhp_drink_sheet.html?orderId=${id}`;
		}
		return `${config.BASE_URL}/invoice/sales_invoice.html?orderId=${id}&prefix=${encodeURIComponent(accessToken || '')}`;
	};

	const getA4PrintUrl = (id) => {
		const accessToken = localStorage.getItem('accessToken');
		if (shouldUseRhpInvoicePrint()) {
			return getRhpA4InvoicePrintUrl(id);
		}
		return `${config.BASE_URL}/invoice/sales_invoice.html?orderId=${id}&prefix=${encodeURIComponent(accessToken || '')}`;
	};


	const handlePrintInvoice = (id) => {
		if (shouldUseRhpInvoicePrint() || isRhpPrintTenantLocal()) {
			markSalePrinted(id);
			window.open(
				getRhpV2InvoicePrintUrl(id, { autoprint: true }),
				'_blank',
				'noopener,noreferrer'
			);
		} else {
			window.open(`${config.BASE_URL}/sales/invoice/${id}`, '_blank', 'noopener,noreferrer');
		}
		handleActionMenuClose();
	};

	const handlePrintInvoiceV2 = (id) => {
		if (id) {
			if (isRhpPrintTenantLocal()) {
				markSalePrinted(id);
			}
			window.open(
				getRhpV2InvoicePrintUrl(id, { autoprint: true }),
				'_blank',
				'noopener,noreferrer'
			);
		}
		handleActionMenuClose();
	};

	const handlePrintInvoiceTest = (id) => {
		if (id) {
			if (isRhpPrintTenantLocal()) {
				markSalePrinted(id);
			}
			window.open(
				getRhpLegacyInvoicePrintUrl(id, {
					autoprint: true,
					statusTab,
				}),
				'_blank',
				'noopener,noreferrer'
			);
		}
		handleActionMenuClose();
	};

	const handlePrintInvoiceElectron = (id) => {
		if (id) {
			if (isRhpPrintTenantLocal()) {
				markSalePrinted(id);
			}
			window.open(
				getRhpElectronInvoicePrintUrl(id, { autoprint: true }),
				'_blank',
				'noopener,noreferrer'
			);
		}
		handleActionMenuClose();
	};

	const handlePrintDeliveryNote = (id) => {
		if (id) {
			window.open(getDeliveryNotePrintUrl(id), '_blank', 'noopener,noreferrer');
		}
	};


	const handlePrintA4Page = (id) => {
		if (id) {
			window.open(getA4PrintUrl(id), '_blank', 'noopener,noreferrer');
		}
	};

	const handlePrintDrinkSheet = (id) => {
		if (id) {
			window.open(getDrinkSheetPrintUrl(id), '_blank', 'noopener,noreferrer');
		}
	};

	const handleReorderClick = (row) => {
		setReorderRow(row || null);
		setReorderConfirmOpen(true);
		handleActionMenuClose();
	};

	const handleReorderConfirmClose = () => {
		setReorderConfirmOpen(false);
		setReorderRow(null);
	};

	const handleReorder = async (row) => {
		handleReorderConfirmClose();
		const orderId = row?.ids || row?.id || row?.order_id;
		if (!orderId) {
			toast.error('Invalid order');
			return;
		}

		const toastId = toast.loading('Preparing reorder...');
		try {
			const data = await fetchProformaInvoice(orderId);
			const details = data?.details || data || {};
			const order =
				(Array.isArray(details.orderDetails) && details.orderDetails[0]) ||
				details.order ||
				{};
			const products = Array.isArray(details.product_details)
				? details.product_details
				: [];

			const customerId =
				order.customer_id ||
				order.customerId ||
				order.cust_id ||
				row.customer_id ||
				row.cust_id;
			if (!customerId) {
				toast.error('Customer not found for this order', { id: toastId });
				return;
			}

			const buyProducts = products
				.filter((p) => !isGetOrFreeInvoiceLine(p))
				.map(mapInvoiceProductForReorder)
				.filter((p) => p.product_id != null && String(p.product_id).trim() !== '');

			if (buyProducts.length === 0) {
				toast.error('No buy products found to reorder', { id: toastId });
				return;
			}

			try {
				await clearCartByCustomer(customerId, PLATFORMS.TELE_CALLING);
			} catch (e) {
			}

			let added = 0;
			for (let i = 0; i < buyProducts.length; i += 1) {
				const mapped = buyProducts[i];
				const cartItem = transformProductToCartItem(mapped, customerId);
				const payload = buildAddToCartPayload(cartItem, customerId);
				payload.is_promoted = '0';
				payload.promotion_id = '';
				await addToCart(payload, PLATFORMS.TELE_CALLING);
				added += 1;
			}

			const customer = {
				id: customerId,
				company: order.company || order.customer_name || order.name || row.customer || '',
				name: order.name || order.customer_name || row.customer || '',
				accound_no: order.accound_no || order.account_no || row.account_no || '',
				account_no: order.account_no || order.accound_no || row.account_no || '',
				address: order.address || order.customer_address || '',
				phone: order.phone || '',
				outstanding_amount: order.outstanding_amount ?? row.prev_due ?? null,
			};

			sessionStorage.setItem('quickOrder_selectedCustomer', JSON.stringify(customer));
			sessionStorage.setItem(
				'quickOrder_customerSearch',
				formatCustomerDisplayLabel(customer)
			);

			toast.success(`Reorder ready (${added} product${added === 1 ? '' : 's'})`, {
				id: toastId,
			});
			window.location.href = `${config.BASE_URL}/sales/quick-order`;
		} catch (error) {
			toast.error(error?.message || 'Reorder failed', { id: toastId });
		}
	};

	return (
		<>
			<Box className="page-container" >
				<Paper className="paper-style">
					<Box sx={{ px: 2, py: 1.2 }}>
						<Grid2
							container
							spacing={1}
							display="flex"
							justifyContent="space-between"
							alignItems="center"
						>
							<Grid2 size={{ xs: 12, md: 2 }}>
								<Typography className="page-title">
									Sales Report
								</Typography>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 3 }}>
								<TextField
									className="input-border"
									fullWidth
									value={searchTerm}
									onChange={(e) => {
										setSearchTerm(e.target.value);
										setPage(0);
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter') handleSubmit();
									}}
									size="small"
									placeholder="Search sales..."
									variant="outlined"
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: '#0e0f0fef' }} />
												</InputAdornment>
											)
										}
									}}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 3 }}>
								<CommonDatePicker
									className="input-border"
									value={dates}
									onChange={handleDateChange}
									format="dd/MM/yyyy"
									character=" - "
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 2 }}>
								<FormControl className="input-border" size="small" fullWidth>
									<Select
										value={orderByFilter}
										onChange={(e) => {
											handleOrderByChange(e.target.value);
										}}
										displayEmpty
									>
										<MenuItem value="">Order By: All</MenuItem>
										{orderByOptions.map((option) => (
											<MenuItem key={option} value={option}>
												{option}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 2 }}>
								<Box sx={{
									display: 'flex',
									gap: { xs: 1, sm: 1 },
								    flexWrap: 'nowrap',
									justifyContent: { xs: 'flex-start', md: 'flex-end' },
									alignItems: 'flex-end'
								}}>
									<Button
										variant="outlined"
										color="black"
										startIcon={<Iconify icon="eva:refresh-outline" width={18} height={18} />}
										onClick={handleClearFilters}
										className="button-variant-outlined"
										fullWidth
									>
										Clear
									</Button>
									<Button
										variant="outlined"
										color="black"
										startIcon={<Iconify icon="eva:download-outline" width={18} height={18} />}
										onClick={handleExport}
										disabled={!sortedData.length}
									
										className="button-variant-outlined"
										fullWidth
									>
										Export
									</Button>
								</Box>
							</Grid2>
						</Grid2>
						<Box>
							<Tabs
								value={statusTab}
								onChange={handleStatusTabChange}
								className="common-tabs"
							>
								<Tab
									label="New"
									value="New"
								/>

								<Tab
									label="Delivered"
									value="Delivered"
								/>
							</Tabs>

						</Box>
					</Box>

					{/* Table Section */}
					<TableContainer sx={{ width: '100%', overflowX: 'auto', mx: { xs: -2, sm: 0 }, px: { xs: 1, sm: 0 } }}>
						<Table size='small' className="table-cell-border" sx={{ tableLayout: { xs: 'auto', sm: 'fixed' }, minWidth: { xs: 800, sm: 'auto' } }}>
							<TableHead>
								<TableRow className="table-cell-header">
									{columns.map((column) => (
										<TableCell
											key={column.id}
											align={column.numeric ? 'right' : column.id === 'sale_status' ? 'center' : 'left'}
											sx={{
												width: column.width ? `${column.width}px` : 'auto',
												fontSize: { xs: '0.7rem', sm: '0.75rem' },
												fontWeight: 600,
												color: '#212b36',
												py: { xs: 0.75, sm: 1 }
											}}
										>
											{column.id === 'name' ? (
												<TableSortLabel
													active={sortBy === 'name'}
													direction={sortBy === 'name' ? sortDirection : 'asc'}
													onClick={() => handleSort('name')}
													hideSortIcon={false}
													sx={{
														'& .MuiTableSortLabel-icon': {
															opacity: '1 !important',
															visibility: 'visible !important',
														},
													}}
												>
													{column.label}
												</TableSortLabel>
											) : (
												column.label
											)}
										</TableCell>
									))}
									<TableCell align='center' sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, color: '#212b36', py: { xs: 0.75, sm: 1 }, width: '50px', px: 0.5 }}>Action</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading
									? Array.from({ length: rowsPerPage }).map((_, index) => (
										<TableRow key={`skeleton-${index}`}>
											{columns.map((column) => (
												<TableCell
													key={column.id}
													align={column.numeric ? 'right' : column.id === 'sale_status' ? 'center' : 'left'}
													sx={{
														width: column.width ? `${column.width}px` : 'auto',
														fontSize: { xs: '0.65rem', sm: '0.7rem' },
														py: { xs: 0.75, sm: 1 }
													}}
												>
													<Skeleton variant='text' />
												</TableCell>
											))}
											<TableCell align='center' sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, py: { xs: 0.75, sm: 1 }, px: 0.5, width: '50px' }}>
												<Skeleton variant='text' />
											</TableCell>
										</TableRow>
									))
									: sortedData.length > 0
										? sortedData.map((row, rowIndex) => (
											<TableRow
												key={row.ids || row.reference_no || rowIndex}
												className="table-cell-style"
												sx={{
													'&:hover': {
														backgroundColor: 'rgba(145, 158, 171, 0.04)'
													},
												}}
											>
												{columns.map((column) => (
													<TableCell
														key={column.id}
														align={column.numeric ? 'right' : column.id === 'sale_status' ? 'center' : 'left'}
														sx={{
															width: column.width ? `${column.width}px` : 'auto'
														}}
													>
														{renderCellContent(column.id, row)}
													</TableCell>
												))}
												<TableCell align='center' sx={{ width: '50px', px: 0.5 }}>
													<IconButton onClick={(event) => handleActionMenuOpen(event, row)} size="small" sx={{ p: 0.5 }}>
														<Iconify icon="mdi:dots-vertical" width={18} height={18} />
													</IconButton>
												</TableCell>
											</TableRow>
										))
										: (
											<TableRow>
												<TableCell colSpan={columns.length + 1} sx={{ py: 4, display: { xs: 'table-cell', sm: 'table-cell' } }}>
													<Typography align='center' sx={{fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
														No records found.
													</Typography>
												</TableCell>
											</TableRow>
										)}
							</TableBody>
							{/* <TableFooter>
								<TableRow 
								sx={{ backgroundColor: '#f5f7fa', '& > *': { borderTop: '2px solid rgba(145, 158, 171, 0.16)' }, 
								'& .MuiTableCell-root': {
									fontSize: { xs: '0.7rem', sm: '0.75rem' },
									color: '#212b36',
									py: { xs: 0.75, sm: 1 }
								} }}>
									{columns.map((column, index) => {
										if (column.id === 'grand_total') {
											return (
												<TableCell key={column.id} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }} align='right'>
													Total
												</TableCell>
											);
										}
										return <TableCell key={column.id} />;
									})}
									<TableCell align='center' />
								</TableRow>
								<TableRow 
								sx={{ backgroundColor: '#f5f7fa', 
								'& .MuiTableCell-root': {
									fontSize: { xs: '0.7rem', sm: '0.75rem' },
									color: '#212b36',
									py: { xs: 0.75, sm: 1 }
								} }}>
									{columns.map((column) => {
										if (column.id === 'grand_total') {
											return (
												<TableCell key={column.id} align='right'>
													{totals.grand_total}
												</TableCell>
											);
										}
										return <TableCell key={column.id} />;
									})}
									<TableCell align='center' />
								</TableRow>
							</TableFooter> */}
						</Table>
					</TableContainer>

					{/* Pagination Section */}
					<Box display="flex" justifyContent={{ xs: 'center', sm: 'flex-end' }} alignItems="center">
						<TablePagination
							component='div'
							count={totalRows}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50, 100]}
							labelRowsPerPage={isCompactTable ? '' : 'Rows per page:'}
							className="pagination-styles"

						/>
					</Box>
				</Paper>
			</Box>
			<Menu
				anchorEl={actionAnchorEl}
				open={Boolean(actionAnchorEl)}
				onClose={handleActionMenuClose}
				sx={{
					'& .MuiMenu-list': {
						width: '180px',

					}
				}}
			>
				{isRhpUser ? (
					<>
						{canEditEposSales && actionRow?.sale_status !== 'Delivered' ? (
							<MenuItem
								sx={{
									fontWeight: 500,
									fontSize: '0.8rem',
								}}
								onClick={() => {
									if (actionRow) {
										window.location.href = `${config.BASE_URL}/sales/epos_sales/edit/${actionRow.ids}`;
									}
									handleActionMenuClose();
								}}
							>
								Edit
							</MenuItem>
						) : null}
						{/* <MenuItem
							sx={{
								fontWeight: 500,
								fontSize: '0.8rem',
							}}
							onClick={() => {
								if (actionRow) {
									handlePrintInvoice(actionRow.ids);
								}
								handleActionMenuClose();
							}}
						>
							Old Invoice
						</MenuItem> */}
						<MenuItem
							sx={{
								fontWeight: 500,
								fontSize: '0.8rem',
							}}
							onClick={() => {
								if (actionRow) {
									handlePrintInvoiceV2(actionRow.ids);
								}
								handleActionMenuClose();
							}}
						>
							Print Invoice
						</MenuItem>
						<MenuItem
							sx={{
								fontWeight: 500,
								fontSize: '0.8rem',
							}}
							onClick={() => {
								if (actionRow) {
									handlePrintInvoiceTest(actionRow.ids);
								}
								handleActionMenuClose();
							}}
						>
							Test Print Invoice
						</MenuItem>
						{userRole === '20' ? (
							<MenuItem
								sx={{
									fontWeight: 500,
									fontSize: '0.8rem',
								}}
								onClick={() => {
									if (actionRow) {
										handleReorderClick(actionRow);
									}
								}}
							>
								Reorder
							</MenuItem>
						) : null}
						<MenuItem
							sx={{
								fontWeight: 500,
								fontSize: '0.8rem',
							}}
							onClick={() => {
								if (actionRow) {
									handlePrintDeliveryNote(actionRow.ids);
								}
								handleActionMenuClose();
							}}
						>
							Print Delivery Note
						</MenuItem>
						<MenuItem
							sx={{
								fontWeight: 500,
								fontSize: '0.8rem',
							}}
							onClick={() => {
								if (actionRow) {
									handlePrintDrinkSheet(actionRow.ids);
								}
								handleActionMenuClose();
							}}
						>
							Print Drink Sheet
						</MenuItem>
						<MenuItem
							sx={{
								fontWeight: 500,
								fontSize: '0.8rem',
							}}
							onClick={() => {
								if (actionRow) {
									handlePrintA4Page(actionRow.ids);
								}
								handleActionMenuClose();
							}}
						>
							Print A4 Page
						</MenuItem>
						<MenuItem
							sx={{
								fontWeight: 500,
								fontSize: '0.8rem',
							}}
							onClick={() => {
								if (actionRow) {
									handleRejectClick(actionRow);
								}
							}}
						>
							Reject
						</MenuItem>
					</>
				) : (
					<>
				{userRole === '20' ? (
					<MenuItem
						sx={{
							fontWeight: 500,
							fontSize: '0.8rem',
						}}
						onClick={() => {
							if (actionRow) {
								handleReorderClick(actionRow);
							}
						}}
					>
						Reorder
					</MenuItem>
				) : null}
				<MenuItem
					sx={{
						fontWeight: 500,
						fontSize: '0.8rem',
					}}
					onClick={() => {
						if (actionRow) {
							handleRejectClick(actionRow);
						}
					}}
				>
					Reject
				</MenuItem>
				{(config.PREFIX === 'eve' || config.PREFIX === 'tsc' || config.PREFIX === 'ebu') && (
					<>
					{String(actionRow?.sale_status || '').toLowerCase() === 'invoiced' && (
					<MenuItem
						sx={{
							fontWeight: 500,
							fontSize: '0.8rem',
						}}
						onClick={() => {
							if (actionRow) {
								handleConfirmInvoice(actionRow.ids);
							}
							handleActionMenuClose();
						}}
					>
						Confirm Invoice
					</MenuItem>
					)}
					<MenuItem
						sx={{
							fontWeight: 500,
							fontSize: '0.8rem',
						}}
						onClick={() => {
							if (actionRow) {
								handlePrintInvoiceV2(actionRow.ids);
							}
							handleActionMenuClose();
						}}
					>
						Print Invoice
					</MenuItem>
					</>
				)}

				{actionRow?.sale_status === 'New' && (
					<MenuItem
						sx={{
							fontWeight: 500,
							fontSize: '0.8rem',
						}}
						onClick={() => {
							if (actionRow) {
								handleHoldClick(actionRow);
							}
						}}
					>
						Hold
					</MenuItem>
				)}
					</>
				)}
			</Menu>
			<Dialog
				open={reorderConfirmOpen}
				onClose={handleReorderConfirmClose}
				maxWidth="xs"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 2,
					}
				}}
			>
				<DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: '#212b36', pb: 1 }}>
					Confirm Reorder
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: '0.875rem', color: '#000' }}>
						Your current cart will be cleared, and the products from this order will be added. Continue?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<Button
						onClick={handleReorderConfirmClose}
						variant="outlined"
						className="button-variant-outlined"
					>
						Cancel
					</Button>
					<Button
						onClick={() => {
							if (reorderRow) {
								handleReorder(reorderRow);
							}
						}}
						variant="contained"
						className="button-variant-contained"
					>
						Reorder
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={showRejectModal}
				onClose={() => setShowRejectModal(false)}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 2,
						maxHeight: '90vh',
						overflow: 'hidden',
					}
				}}
			>
				<DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: '#212b36', pb: 1 }}>
					Reject Order
				</DialogTitle>
				<DialogContent
					dividers
					sx={{
						overflow: 'hidden',
						'&::-webkit-scrollbar': {
							display: 'none',
						},
						scrollbarWidth: 'none',
					}}
				>
					{rejectData && (
						<Table size="small" className="table-cell-border" sx={{ mb: 2 }}>
							<TableBody
								sx={{

									'& .labelCell': {
										fontSize: '0.75rem',
										color: '#212b36',
									},
								}}
							>
								<TableRow >
									<TableCell width="40%">Order Id</TableCell>
									<TableCell className="labelCell">{rejectData.reference_no}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Customer (A/C)</TableCell>
									<TableCell className="labelCell">{rejectData.customer}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Order Amount</TableCell>
									<TableCell className="labelCell">{rejectData.grand_total}</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Previous Dues</TableCell>
									<TableCell className="labelCell">{rejectData.prev_due}</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					)}
					<TextField
						className="form-input-border"
						label="Reason Note"
						value={rejectComment}
						onChange={(event) => setRejectComment(event.target.value)}
						multiline
						minRows={3}
						size="small"
						fullWidth
						sx={{
							'& .MuiOutlinedInput-root': {

								backgroundColor: '#f5f7fa',
								fontSize: '0.875rem',
								'&:hover': {
									backgroundColor: '#fff'
								},
								'&.Mui-focused': {
									backgroundColor: '#fff'
								}
							},
							'& .MuiInputLabel-root': {
								fontSize: '0.875rem',
								fontWeight: 500,
								backgroundColor: '#fff',
								px: 0.5,
								'&.MuiInputLabel-shrink': {
									transform: 'translate(14px, -9px) scale(0.75)',
								}
							},
						}}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<Button
						onClick={() => setShowRejectModal(false)}
						variant="outlined"
						className="button-variant-outlined"
					>
						Cancel
					</Button>
					<Button
						onClick={handleRejectSubmit}
						variant="contained"

						className="button-variant-contained"
					>
						Update
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={showHoldModal}
				onClose={() => setShowHoldModal(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 2,
						maxHeight: '90vh',
						overflow: 'hidden',
					}
				}}
			>
				<DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: '#212b36', pb: 1 }}>
					Hold Order
				</DialogTitle>
				<DialogContent
					dividers
					sx={{
						py: 1,
						overflow: 'hidden',
						'&::-webkit-scrollbar': {
							display: 'none',
						},
						scrollbarWidth: 'none',
					}}
				>
					<TextField
						className="form-input-border"
						label="Reason Note"
						value={holdReason}
						onChange={(event) => setHoldReason(event.target.value)}
						multiline
						minRows={3}
						size="small"
						fullWidth
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: '#f5f7fa',
								fontSize: '0.875rem',
								'&:hover': {
									backgroundColor: '#fff'
								},
								'&.Mui-focused': {
									backgroundColor: '#fff'
								}
							},
							'& .MuiInputLabel-root': {
								fontSize: '0.875rem',
								fontWeight: 500,
								backgroundColor: '#fff',
								px: 0.5,
								'&.MuiInputLabel-shrink': {
									transform: 'translate(14px, -9px) scale(0.75)',
								}
							},
						}}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<Button
						onClick={() => setShowHoldModal(false)}
						variant="outlined"
						className="button-variant-outlined"
					>
						Cancel
					</Button>
					<Button
						onClick={handleHoldSubmit}
						variant="contained"
						className="button-variant-contained"
					>
						Update
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
export default EposSales;